import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function Products() {
  const { t, i18n } = useTranslation('app');
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState('Y');
  const [expandedItems, setExpandedItems] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/data/products.json');
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const toggleItemExpand = (packageCode, itemIndex) => {
    const key = `${packageCode}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (!products) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600">{t('common.error')}</p>
      </div>
    );
  }

  const currentSeries = products.series.find(s => s.id === selectedSeries);
  const isZh = i18n.language === 'zh';

  // Ensure all packages have series and descriptions (backward compatibility)
  const enrichedPackages = currentSeries?.packages?.map(pkg => ({
    ...pkg,
    series: pkg.series || 'Y',
    description_en: pkg.description_en || pkg.name_en || '',
    description_cn: pkg.description_cn || pkg.name_cn || ''
  })) || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <style>{`
        /* Mobile-first CSS approach */
        
        /* 8pt spacing scale */
        .spacing-xs { margin: 4px; }
        .spacing-sm { margin: 8px; }
        .spacing-md { margin: 16px; }
        .spacing-lg { margin: 24px; }
        .spacing-xl { margin: 32px; }
        
        /* Touch targets - minimum 44x44px */
        button { min-height: 48px; }
        
        /* Smooth scrolling for category pills */
        .category-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          padding-left: 16px;
          padding-right: 16px;
          margin: 16px 0;
        }
        
        .category-scroll::-webkit-scrollbar {
          height: 4px;
        }
        
        .category-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }
        
        .category-pill {
          flex-shrink: 0;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          transition: all 0.3s ease;
          border: 2px solid #e5e7eb;
          background: white;
          color: #666;
        }
        
        .category-pill.active {
          background: #2563eb;
          border-color: #2563eb;
          color: white;
        }
        
        .category-pill:active {
          transform: scale(0.98);
        }
        
        /* Product card animations */
        .product-card {
          animation: fadeIn 0.3s ease-in;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive grid */
        @media (max-width: 430px) {
          .products-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px;
          }
          
          .header-sticky {
            position: sticky;
            top: 0;
            z-index: 20;
            background: white;
            border-bottom: 1px solid #e5e7eb;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          
          .hero-mobile {
            height: 240px;
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            padding: 24px 16px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            color: white;
          }
          
          .hero-title {
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            margin-bottom: 8px;
          }
          
          .hero-subtitle {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            margin-bottom: 16px;
            opacity: 0.95;
          }
          
          .cta-hero {
            background: white;
            color: #2563eb;
            border: none;
            padding: 10px 16px;
            border-radius: 8px;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            align-self: flex-start;
            transition: all 0.3s ease;
          }
          
          .cta-hero:active {
            transform: scale(0.98);
            opacity: 0.9;
          }
          
          .product-card {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transition: all 0.3s ease;
          }
          
          .product-card:active {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          }
          
          .product-image {
            width: 100%;
            aspect-ratio: 1 / 1;
            background: #f3f4f6;
            object-fit: contain;
            padding: 16px;
          }
          
          .product-content {
            padding: 16px;
          }
          
          .product-code {
            font-size: 12px;
            font-family: monospace;
            color: #999;
            margin-bottom: 4px;
          }
          
          .product-name {
            font-size: 20px;
            font-weight: 600;
            color: #111;
            margin-bottom: 6px;
            line-height: 1.2;
          }
          
          .product-description {
            font-size: 14px;
            color: #666;
            margin-bottom: 12px;
            line-height: 1.4;
          }
          
          .price-badge {
            background: #f0f4ff;
            border-left: 4px solid #2563eb;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }
          
          .price-value {
            font-size: 28px;
            font-weight: 700;
            color: #2563eb;
            line-height: 1;
          }
          
          .price-label {
            font-size: 12px;
            color: #666;
            margin-top: 4px;
          }
          
          .bonus-badge {
            background: #ecfdf5;
            border-left: 4px solid #10b981;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }
          
          .bonus-title {
            font-size: 12px;
            font-weight: 600;
            color: #059669;
            margin-bottom: 4px;
          }
          
          .bonus-item {
            font-size: 14px;
            color: #047857;
            margin: 4px 0;
          }
          
          .accordion-item {
            border-top: 1px solid #e5e7eb;
            padding: 12px 0;
          }
          
          .accordion-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 14px;
            font-weight: 500;
            color: #111;
            cursor: pointer;
            padding: 8px 0;
            background: none;
            border: none;
            width: 100%;
          }
          
          .accordion-header:active {
            opacity: 0.7;
          }
          
          .accordion-icon {
            font-size: 18px;
            transition: transform 0.3s ease;
          }
          
          .accordion-icon.expanded {
            transform: rotate(180deg);
          }
          
          .accordion-content {
            font-size: 13px;
            color: #666;
            line-height: 1.6;
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease;
          }
          
          .accordion-content.expanded {
            max-height: 500px;
            margin-top: 8px;
          }
          
          .cta-button {
            width: 100%;
            height: 48px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 8px;
          }
          
          .cta-button:active {
            background: #1d4ed8;
            transform: scale(0.98);
          }
          
          .sticky-cta {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: white;
            border-top: 1px solid #e5e7eb;
            padding: 12px 16px;
            z-index: 30;
            box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
          }
          
          .sticky-cta-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }
          
          .sticky-cta-price {
            display: flex;
            flex-direction: column;
          }
          
          .sticky-cta-label {
            font-size: 12px;
            color: #666;
            line-height: 1;
          }
          
          .sticky-cta-value {
            font-size: 24px;
            font-weight: 700;
            color: #2563eb;
            line-height: 1;
          }
          
          .sticky-cta-button {
            flex: 1;
            height: 44px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            font-size: 15px;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .sticky-cta-button:active {
            background: #1d4ed8;
            transform: scale(0.98);
          }
        }
        
        /* Tablet and up */
        @media (min-width: 431px) {
          .products-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 24px;
            padding: 32px;
          }

          .sticky-cta {
            display: none;
          }

          .category-scroll {
            flex-wrap: wrap;
            padding-left: 32px;
            padding-right: 32px;
            margin: 24px 0;
          }

          .cta-button {
            width: 100%;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 8px;
          }

          .cta-button:hover {
            background: #1d4ed8;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
          }

          .cta-button:active {
            background: #1d4ed8;
            transform: scale(0.98);
          }
        }
      `}</style>

      {/* Sticky Mobile Header */}
      <div className="header-sticky">
        <h1 className="text-xl font-bold text-gray-900">Wellis</h1>
        <div className="flex gap-4">
          <button className="p-2 hover:bg-gray-100 rounded-lg">
            🔍
          </button>
        </div>
      </div>

      {/* Hero Section */}
      {currentSeries && (
        <div className="hero-mobile">
          <h2 className="hero-title">
            {isZh ? currentSeries.name_cn : currentSeries.name_en}
          </h2>
          <p className="hero-subtitle">
            {isZh ? currentSeries.description_cn : currentSeries.description_en}
          </p>
          <button className="cta-hero">
            {isZh ? '探索' : 'Explore'}
          </button>
        </div>
      )}

      {/* Series Tabs / Category Pills */}
      <div className="category-scroll">
        {products.series.map((series) => (
          <button
            key={series.id}
            onClick={() => setSelectedSeries(series.id)}
            className={`category-pill ${selectedSeries === series.id ? 'active' : ''}`}
          >
            {series.id}-Series
          </button>
        ))}
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {currentSeries && enrichedPackages.length > 0 ? (
          enrichedPackages.map((pkg) => (
            <div key={pkg.code} className="product-card">
              {/* Product Image */}
              <div className="relative">
                {pkg.image_url ? (
                  <img
                    src={(isZh && pkg.image_url_cn) ? pkg.image_url_cn : pkg.image_url}
                    alt={pkg.code}
                    className="product-image"
                    loading="lazy"
                  />
                ) : (
                  <div className="product-image flex items-center justify-center">
                    <span className="text-gray-400">{pkg.code}</span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="product-content">
                <p className="product-code">{pkg.code}</p>
                <h3 className="product-name">
                  {isZh ? pkg.name_cn : pkg.name_en}
                </h3>
                <p className="product-description">
                  {isZh ? pkg.description_cn : pkg.description_en}
                </p>

                {/* Price Badge */}
                <div className="price-badge">
                  <div className="price-value">
                    {pkg.price.toLocaleString()}
                  </div>
                  <div className="price-label">
                    {isZh ? '信用点数' : 'Credit Points'}
                  </div>
                </div>

                {/* Welcome Bonus */}
                {(pkg.cash_amount > 0 || pkg.product_credit > 0) && (
                  <div className="bonus-badge">
                    <div className="bonus-title">
                      {isZh ? '欢迎奖励' : 'Welcome Bonus'}
                    </div>
                    {pkg.cash_amount > 0 && (
                      <div className="bonus-item">
                        {pkg.cash_amount} {isZh ? '现金点数' : 'Cash Points'}
                      </div>
                    )}
                    {pkg.product_credit > 0 && (
                      <div className="bonus-item">
                        {pkg.product_credit} {isZh ? '产品点数' : 'Product Points'}
                      </div>
                    )}
                  </div>
                )}

                {/* Accordion - What's Included */}
                {pkg.items.length > 0 && (
                  <div className="mt-4">
                    {pkg.items.map((item, idx) => {
                      const itemKey = `${pkg.code}-${idx}`;
                      const isExpanded = expandedItems[itemKey];

                      return (
                        <div key={idx} className="accordion-item">
                          <button
                            className="accordion-header"
                            onClick={() => toggleItemExpand(pkg.code, idx)}
                          >
                            <span>{isZh ? item.title_cn : item.title_en}</span>
                            <span className={`accordion-icon ${isExpanded ? 'expanded' : ''}`}>
                              ▼
                            </span>
                          </button>
                          <div className={`accordion-content ${isExpanded ? 'expanded' : ''}`}>
                            {isZh ? item.details_cn : item.details_en}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={() => navigate('/signup')}
                  className="cta-button"
                >
                  {isZh ? '立即了解' : 'Learn More'}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-600 text-lg">
              {isZh ? '此系列暂无产品' : 'No products in this series'}
            </p>
          </div>
        )}
      </div>

      {/* Sticky Bottom CTA */}
      {currentSeries && enrichedPackages.length > 0 && (
        <div className="sticky-cta">
          <div className="sticky-cta-content">
            <div className="sticky-cta-price">
              <span className="sticky-cta-label">
                {isZh ? '起价' : 'Starting from'}
              </span>
              <span className="sticky-cta-value">
                {Math.min(...enrichedPackages.map(p => p.price)).toLocaleString()}
              </span>
            </div>
            <button
              onClick={() => navigate('/signup')}
              className="sticky-cta-button"
            >
              {isZh ? '立即购买' : 'Buy Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
